<template>
  <BaseInput
    name="walletAddress"
    :label="$t('WalletAddressField.label.walletAddress')"
    :errors="errors"
  >
    <SkeleLoader v-if="isLoading" lines="1" />
    <div class="wallet-input__wrapper" v-else>
      <input
        type="text"
        id="walletAddress"
        :class="{ hasErrors: errors }"
        name="walletAddress"
        :placeholder="$t('WalletAddressField.placeholder.walletAddress')"
        autocomplete="off"
        :readonly="isReadOnly"
        :value="removeWhiteSpace(walletAddress)"
        @keyup="$emit('update:walletAddress', $event.target.value)"
        @blur="$emit('update:walletAddress', $event.target.value)"
      />
      <component
        :is="wallet"
        @updateWallet="
          (walletAddress) => $emit('update:walletAddress', walletAddress)
        "
        v-if="shouldShowWallet"
        v-bind="walletProps"
      />
    </div>
  </BaseInput>
</template>

<script setup>
import { onBeforeMount, ref, watch, computed } from "vue";

import { useOrderFormStore } from "@/stores/forms/orderFormStore";
import { useGlobalStore } from "@/stores/globalStore";

import useBlockchainFilters from "@/composables/filters/useBlockchainFilters";
import { removeWhiteSpace } from "@/composables/helpers/useStringHelpers";

import SkeleLoader from "@/components/ui/loaders/SkeleLoader.vue";
import BaseInput from "@/components/ui/inputs/BaseInput.vue";
import WalletConnectButton from "@/components/ui/buttons/WalletConnectButton.vue";
import WalletConnectV2Button from "@/components/ui/buttons/WalletConnectV2Button.vue";
import MetamaskButton from "@/components/ui/buttons/MetamaskButton.vue";

const { getMetaMaskIdChainBySelectedBlockchain } = useBlockchainFilters();
const orderStore = useOrderFormStore();
const globalStore = useGlobalStore();

defineProps({
  walletAddress: {
    type: String,
    default: "",
  },
  errors: {
    type: String,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isReadOnly: {
    type: Boolean,
    default: false,
  },
});

defineEmits(["update:walletAddress"]);

const wallet = ref(null);
const shouldShowWallet = ref(false);
const evmChainId = ref(1);

const getDisplayWallet = () => {
  if (globalStore.getMerchantSettings?.disableMetamask) {
    shouldShowWallet.value = false;
    return;
  }
  const activeCoin = orderStore.getActiveStore.form.coinType.value;
  const activeBlockchain = orderStore.getActiveStore.form.blockchain.value;
  const { data } = orderStore.getActiveStore.coinData;
  const currentChainEvmChainId = getMetaMaskIdChainBySelectedBlockchain(
    data,
    activeCoin,
    activeBlockchain
  );
  switch (globalStore.getMerchantSettings?.walletType) {
    case "WALLET_CONNECT":
      wallet.value = WalletConnectV2Button;
      break;
    case "METAMASK":
    default:
      wallet.value = MetamaskButton;
  }
  if (currentChainEvmChainId) {
    shouldShowWallet.value = true;
    evmChainId.value = currentChainEvmChainId;
    return;
  }
  shouldShowWallet.value = false;
};


const walletProps = computed(() => {
  return {
    evmChainId: evmChainId.value,
  };
});

watch(
  () => orderStore.getActiveStore.form.blockchain.value,
  () => {
    getDisplayWallet();

  },
  { deep: true }
);

onBeforeMount(() => {
  getDisplayWallet();
});
</script>

<style lang="scss">
.wallet-input__wrapper {
  display: flex;
  gap: 1rem;
}
</style>
